import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { CircularProgress, makeStyles, Menu, MenuItem } from '@material-ui/core'
import Icon from '../../atoms/Icon'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import useCSV from '../../../hooks/useCSV'
import { flattenVisibleRows, flattenAllRows, buildHeaders, buildCSV, buildTSV } from './lib/exportRows'
import GroupingBreadcrumb from './GroupingBreadcrumb'
import LazyExpandDialog from './LazyExpandDialog'

const DEPTH_BACKGROUNDS = [
  'var(--opal-surface-default, #fff)',
  'var(--color-neutral-50, #fafafa)',
  'var(--color-neutral-100, #f5f5f5)',
  'var(--color-neutral-150, #efefef)',
  'var(--color-neutral-200, #eaeaea)',
  'var(--color-neutral-200, #eaeaea)'
]

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  scroll: {
    flex: 1,
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    tableLayout: 'fixed'
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 2
  },
  th: {
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 'var(--font-weight-semibold, 600)',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: 'var(--color-neutral-700, #555)',
    background: 'var(--opal-surface-default, #fff)',
    borderBottom: '2px solid var(--opal-border-default, #ddd)',
    whiteSpace: 'nowrap',
    userSelect: 'none'
  },
  thSortable: {
    cursor: 'pointer',
    '&:hover': { color: 'var(--color-neutral-900, #222)' }
  },
  thSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    background: 'var(--opal-surface-default, #fff)'
  },
  thLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  td: {
    padding: '4px 10px',
    height: 30,
    fontWeight: 'var(--font-weight-light, 400)',
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tdSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: 'inherit'
  },
  tdNegative: {
    color: 'var(--color-danger-500, #d32f2f)'
  },
  tdPositive: {
    color: 'var(--color-success-700, #2e7d32)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  row: {
    '&:hover td': {
      background: 'var(--color-primary-50, rgba(0, 100, 255, 0.04))'
    }
  },
  benchmarkRow: {
    background: 'var(--color-neutral-200, #eaeaea)',
    '& td': {
      background: 'var(--color-neutral-200, #eaeaea)',
      borderTop: 0,
      borderBottom: 0,
      color: 'var(--color-neutral-700, #555)',
      fontStyle: 'italic',
      fontWeight: 'var(--font-weight-light, 400)'
    },
    // Neg/pos coloring on benchmark cells must beat the row's color rule.
    // .benchmarkRow td has specificity (0,2,0); plain .tdNegative is (0,1,0)
    // and would lose. Matching .benchmarkRow td.tdNegative bumps to (0,3,0).
    '& td.$tdNegative': {
      color: 'var(--color-danger-500, #d32f2f)'
    },
    '& td.$tdPositive': {
      color: 'var(--color-success-700, #2e7d32)'
    },
    // Left-edge accent on the first cell so benchmarks stay distinct from
    // group rows at deep grouping levels where DEPTH_BACKGROUNDS[4]/[5]
    // collide with the benchmark background. inset box-shadow rather than
    // border-left to avoid layout shift on the sticky __name td.
    '& td:first-child': {
      boxShadow: 'inset 3px 0 0 0 var(--color-primary-400, #3a83f5)'
    }
  },
  dash: {
    color: 'var(--color-neutral-400, #aaa)'
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0
  },
  nameCellClickable: {
    cursor: 'pointer'
  },
  expandIcon: {
    flexShrink: 0,
    transition: 'transform 150ms ease'
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)'
  },
  expandSpacer: {
    display: 'inline-block',
    width: 14,
    flexShrink: 0
  },
  nameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tfoot: {
    position: 'sticky',
    bottom: 0,
    zIndex: 2
  },
  totalTd: {
    padding: '6px 10px',
    height: 32,
    fontWeight: 'var(--font-weight-bold, 700)',
    // Inset box-shadow renders reliably inside the cell box even when the
    // row is sticky; a plain border-top can paint between scrolling layers
    // and disappear as tbody scrolls under the tfoot.
    boxShadow: 'inset 0 2px 0 0 var(--color-neutral-500, #888)',
    borderTop: 0,
    background: 'var(--opal-surface-default, #fff)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  contextMenuItem: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32
  }
}))

/**
 * Renders a benchmark `<tr>` outside the TanStack row model. Benchmarks are
 * stored on `row.benchmarks` (and `totalRow.benchmarks`) and rendered manually
 * so they remain pinned immediately after their parent regardless of column
 * sort direction. Cells are matched by column id (which equals the user's
 * column instance id), so any benchmark-eligible material column lights up
 * with its benchmark counterpart value automatically.
 */
function renderBenchmarkRow ({ benchmark, rowKey, depth, columns, classes, formatter }) {
  return (
    <tr key={rowKey} className={classes.benchmarkRow}>
      {columns.map(col => {
        const meta = col.columnDef.meta
        const isName = col.id === '__name'
        const isRight = meta?.align === 'right' || !isName
        const value = isName ? benchmark.name : benchmark[col.id]
        // Neg/pos coloring matches regular rows so a -2% benchmark return is
        // red and a +5% returns-formatted value is green. Specificity-wise,
        // .tdNegative / .tdPositive are JSS classes generated alongside .td
        // and .benchmarkRow; the more specific selector for the cell wins,
        // so explicit colors on the cell override the row's neutral-700.
        const isNegative = !isName && typeof value === 'number' && value < 0
        const isPositiveGainStyled = !isName && meta?.format === 'returns' && typeof value === 'number' && value > 0
        return (
          <td
            key={col.id}
            className={clsx(classes.td, {
              [classes.tdSticky]: isName,
              [classes.tdRight]: isRight && !isName,
              [classes.tdNegative]: isNegative,
              [classes.tdPositive]: isPositiveGainStyled
            })}
            style={{ width: col.getSize() }}
          >
            {isName
              ? (
                <div className={classes.nameCell} style={{ paddingLeft: depth * 20 + 8 + 14 }}>
                  <span className={classes.nameText}>{value}</span>
                </div>
              )
              : (value == null
                ? <span className={classes.dash}>--</span>
                : formatter(value, meta?.format)
              )}
          </td>
        )
      })}
    </tr>
  )
}

function buildColumns (columnDefinitions, formatter, classes, onToggleExpand) {
  const nameColumn = {
    id: '__name',
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    minSize: 160,
    enableSorting: true,
    cell: ({ row, getValue }) => {
      const canExpand = row.getCanExpand()
      const depth = row.depth
      const isFetchingChildren = row.original?._subRowsFetching

      return (
        <div
          className={clsx(classes.nameCell, { [classes.nameCellClickable]: canExpand })}
          style={{ paddingLeft: depth * 20 + 8 }}
          onClick={canExpand ? () => onToggleExpand(row) : undefined}
        >
          {canExpand ? (
            isFetchingChildren
              ? <CircularProgress size={12} style={{ marginRight: 2, flexShrink: 0 }} />
              : (
                <Icon
                  additionalClasses={clsx(classes.expandIcon, { [classes.expandIconExpanded]: row.getIsExpanded() })}
                  name='chevronRight'
                  customSize={14}
                />
              )
          ) : (
            <span className={classes.expandSpacer} />
          )}
          <span className={classes.nameText}>{getValue()}</span>
        </div>
      )
    }
  }

  const dataColumns = columnDefinitions.map(col => ({
    id: col.accessorKey,
    accessorKey: col.accessorKey,
    header: col.header,
    size: 130,
    minSize: 90,
    enableSorting: true,
    meta: { format: col.format, measureKind: col.measureKind, align: col.align },
    cell: ({ getValue }) => {
      const value = getValue()
      if (value == null) return <span className={classes.dash}>--</span>
      return formatter(value, col.format)
    }
  }))

  return [nameColumn, ...dataColumns]
}

export default function PortfolioExplorerGrid ({ rows, columnDefinitions, totalRow, onExpandRow, groupings = [], expandToLevelLazy, expandAllChildrenLazy, lazyExpandState, cancelLazyExpand, viewName, asOfDate }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const { downloadCSVFile } = useCSV()
  const [sorting, setSorting] = useState([])
  const [expanded, setExpanded] = useState({})
  const [contextMenu, setContextMenu] = useState(null)
  const [hoveredDepth, setHoveredDepth] = useState(null)
  const [pendingExport, setPendingExport] = useState(null)
  const exportExpandStartedRef = useRef(false)

  const handleExpandRow = useCallback((row) => {
    if (onExpandRow) onExpandRow(row)
  }, [onExpandRow])

  const toggleExpandRow = useCallback((row) => {
    if (!row.getCanExpand()) return
    const isFetchingChildren = row.original?._subRowsFetching
    const needsLazyLoad = row.original?._childrenLoaded === false
    if (isFetchingChildren) return

    const toggle = row.getToggleExpandedHandler()
    if (needsLazyLoad) {
      handleExpandRow(row.original)
      if (!row.getIsExpanded()) {
        if (document.startViewTransition) {
          document.startViewTransition(() => toggle())
        } else {
          toggle()
        }
      }
    } else {
      if (document.startViewTransition) {
        document.startViewTransition(() => toggle())
      } else {
        toggle()
      }
    }
  }, [handleExpandRow])

  const expandAllDescendants = useCallback((row) => {
    const ids = {}
    const walk = (r) => {
      if (r.getCanExpand()) {
        ids[r.id] = true
        if (r.subRows) r.subRows.forEach(walk)
      }
    }
    walk(row)
    setExpanded(prev => ({ ...prev, ...ids }))
  }, [])

  const [pendingExpandAllId, setPendingExpandAllId] = useState(null)

  const collapseAllDescendants = useCallback((row) => {
    const idsToRemove = new Set()
    const walk = (r) => {
      idsToRemove.add(r.id)
      if (r.subRows) r.subRows.forEach(walk)
    }
    if (row.subRows) row.subRows.forEach(walk)
    idsToRemove.add(row.id)
    setExpanded(prev => {
      const next = { ...prev }
      idsToRemove.forEach(id => delete next[id])
      return next
    })
  }, [])

  const handleContextMenu = useCallback((e, row) => {
    e.preventDefault()
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, row })
  }, [])

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const columns = useMemo(
    () => buildColumns(columnDefinitions, formatter, classes, toggleExpandRow),
    [columnDefinitions, formatter, classes, toggleExpandRow]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows: row => row._placeholder ? undefined : row.data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  useEffect(() => {
    if (pendingExpandAllId == null) return
    const row = table.getRow(pendingExpandAllId)
    if (row) expandAllDescendants(row)
    setPendingExpandAllId(null)
  }, [pendingExpandAllId, table, expandAllDescendants])

  const syncExpandToLevel = useCallback((targetDepth) => {
    const ids = {}
    for (const row of table.getCoreRowModel().flatRows) {
      if (row.depth < targetDepth && row.getCanExpand()) {
        ids[row.id] = true
      }
    }
    setExpanded(ids)
  }, [table])

  const expandToLevel = useCallback(async (targetDepth) => {
    const hasUnloaded = table.getCoreRowModel().flatRows.some(row =>
      row.depth < targetDepth && row.original?._childrenLoaded === false
    )
    if (hasUnloaded && expandToLevelLazy) {
      const result = await expandToLevelLazy(targetDepth, groupings)
      syncExpandToLevel(result?.targetDepth ?? targetDepth)
    } else {
      syncExpandToLevel(targetDepth)
    }
  }, [table, expandToLevelLazy, groupings, syncExpandToLevel])

  const collapseToLevel = useCallback((targetDepth) => {
    const keep = {}
    for (const row of table.getCoreRowModel().flatRows) {
      if (row.depth < targetDepth && row.getIsExpanded()) {
        keep[row.id] = true
      }
    }
    setExpanded(keep)
  }, [table])

  const executeExport = useCallback((scope, format) => {
    const headers = buildHeaders(columnDefinitions)
    const flatRows = scope === 'visible'
      ? flattenVisibleRows(table.getRowModel().rows, columnDefinitions, formatter, totalRow)
      : flattenAllRows(rows, columnDefinitions, formatter, totalRow)

    if (format === 'csv') {
      const csv = buildCSV(flatRows, headers)
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const namePart = viewName || 'Portfolio Export'
      const datePart = asOfDate ? ` as of ${asOfDate}` : ''
      downloadCSVFile({ url, fileName: `${namePart}${datePart}.csv` })
    } else {
      const tsv = buildTSV(flatRows, headers)
      // TODO: show toast on clipboard failure
      navigator.clipboard.writeText(tsv).catch(() => {})
    }
  }, [columnDefinitions, formatter, totalRow, rows, table, downloadCSVFile, viewName, asOfDate])

  const handleExport = useCallback(({ scope, format }) => {
    if (scope === 'all' && expandToLevelLazy) {
      const hasUnloaded = table.getCoreRowModel().flatRows.some(
        row => row.original?._childrenLoaded === false
      )
      if (hasUnloaded) {
        exportExpandStartedRef.current = false
        setPendingExport({ format })
        expandToLevelLazy(groupings.length, groupings)
        return
      }
    }
    executeExport(scope, format)
  }, [expandToLevelLazy, table, groupings, executeExport])

  useEffect(() => {
    if (pendingExport && lazyExpandState) {
      exportExpandStartedRef.current = true
    }
    if (pendingExport && !lazyExpandState && exportExpandStartedRef.current) {
      executeExport('all', pendingExport.format)
      setPendingExport(null)
      exportExpandStartedRef.current = false
    }
  }, [pendingExport, lazyExpandState, executeExport])

  return (
    <div className={classes.root}>
      <GroupingBreadcrumb
        groupings={groupings}
        onExpandToLevel={expandToLevel}
        onCollapseToLevel={collapseToLevel}
        highlightedDepth={hoveredDepth}
        onExport={handleExport}
      />
      <div className={classes.scroll}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta
                  const isRight = meta?.align === 'right' || header.column.id !== '__name'
                  return (
                    <th
                      key={header.id}
                      className={clsx(classes.th, {
                        [classes.thSticky]: header.column.id === '__name',
                        [classes.thSortable]: header.column.getCanSort()
                      })}
                      style={{
                        width: header.column.getSize(),
                        textAlign: isRight && header.column.id !== '__name' ? 'right' : 'left'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className={classes.thLabel}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && <Icon name='up' customSize={12} />}
                        {header.column.getIsSorted() === 'desc' && <Icon name='down' customSize={12} />}
                      </span>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.flatMap(row => {
              if (row.original?._placeholder) return []
              const depthBg = DEPTH_BACKGROUNDS[Math.min(row.depth, 5)]
              const elements = [
                <tr
                  key={row.id}
                  className={classes.row}
                  style={{ background: depthBg }}
                  onContextMenu={(e) => handleContextMenu(e, row)}
                  onMouseEnter={() => setHoveredDepth(row.depth)}
                  onMouseLeave={() => setHoveredDepth(null)}
                >
                  {row.getVisibleCells().map(cell => {
                    const meta = cell.column.columnDef.meta
                    const isRight = meta?.align === 'right' || cell.column.id !== '__name'
                    const rawValue = cell.getValue()
                    const isNegative = typeof rawValue === 'number' && rawValue < 0
                    const isPositiveGainStyled = meta?.format === 'returns'
                    return (
                      <td
                        key={cell.id}
                        className={clsx(classes.td, {
                          [classes.tdSticky]: cell.column.id === '__name',
                          [classes.tdRight]: isRight && cell.column.id !== '__name',
                          [classes.tdNegative]: isNegative,
                          [classes.tdPositive]: isPositiveGainStyled && typeof rawValue === 'number' && rawValue > 0
                        })}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              ]

              // Benchmarks follow DDPTable sibling-row semantics: visible
              // whenever the parent row is visible, regardless of the parent's
              // own expand state. They disappear only when a grandparent is
              // collapsed (which removes this row from the row model entirely).
              const benchmarks = row.original?.benchmarks
              if (benchmarks?.length) {
                benchmarks.forEach((b, bIdx) => {
                  elements.push(renderBenchmarkRow({
                    benchmark: b,
                    rowKey: `${row.id}-bench-${bIdx}`,
                    depth: row.depth + 1,
                    columns: table.getAllColumns(),
                    classes,
                    formatter
                  }))
                })
              }

              return elements
            })}
          </tbody>
          {totalRow && (
            <tfoot className={classes.tfoot}>
              <tr>
                {table.getAllColumns().map(col => {
                  const meta = col.columnDef.meta
                  const isName = col.id === '__name'
                  const isRight = meta?.align === 'right' || !isName
                  const value = isName ? totalRow.name : totalRow[col.id]
                  return (
                    <td
                      key={col.id}
                      className={clsx(classes.totalTd, {
                        [classes.tdSticky]: isName,
                        [classes.tdRight]: isRight && !isName,
                        [classes.tdNegative]: typeof value === 'number' && value < 0
                      })}
                      style={{ width: col.getSize() }}
                    >
                      {isName
                        ? <span style={{ paddingLeft: 8 }}>{value}</span>
                        : (value == null
                          ? <span className={classes.dash}>--</span>
                          : formatter(value, meta?.format)
                        )}
                    </td>
                  )
                })}
              </tr>
              {totalRow.benchmarks?.map((b, bIdx) => renderBenchmarkRow({
                benchmark: b,
                rowKey: `total-bench-${bIdx}`,
                depth: 0,
                columns: table.getAllColumns(),
                classes,
                formatter
              }))}
            </tfoot>
          )}
        </table>
      </div>
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu?.row.getCanExpand() ? [
          <MenuItem
            key='toggle'
            onClick={() => { toggleExpandRow(contextMenu.row); closeContextMenu() }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name={contextMenu.row.getIsExpanded() ? 'collapse' : 'expand'} customSize={14} />
              {contextMenu.row.getIsExpanded() ? 'Collapse' : 'Expand'}
            </span>
          </MenuItem>,
          <MenuItem
            key='expand-all'
            onClick={async () => {
              const row = contextMenu.row
              closeContextMenu()
              if (expandAllChildrenLazy) {
                await expandAllChildrenLazy(row.original)
                setPendingExpandAllId(row.id)
              } else {
                expandAllDescendants(row)
              }
            }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name='unfoldMore' customSize={14} />
              Expand All Children
            </span>
          </MenuItem>,
          <MenuItem
            key='collapse-all'
            disabled={!contextMenu.row.getIsExpanded()}
            onClick={() => { collapseAllDescendants(contextMenu.row); closeContextMenu() }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name='unfoldLess' customSize={14} />
              Collapse All Children
            </span>
          </MenuItem>
        ] : (
          <MenuItem disabled>
            <span className={classes.contextMenuItem}>No actions available</span>
          </MenuItem>
        )}
      </Menu>
      <LazyExpandDialog
        open={!!lazyExpandState}
        state={lazyExpandState}
        onCancel={cancelLazyExpand || (() => {})}
      />
    </div>
  )
}

PortfolioExplorerGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnDefinitions: PropTypes.arrayOf(PropTypes.shape({
    accessorKey: PropTypes.string,
    header: PropTypes.string,
    format: PropTypes.string,
    measureKind: PropTypes.string,
    align: PropTypes.string
  })).isRequired,
  totalRow: PropTypes.object,
  onExpandRow: PropTypes.func,
  groupings: PropTypes.arrayOf(PropTypes.string),
  expandToLevelLazy: PropTypes.func,
  expandAllChildrenLazy: PropTypes.func,
  lazyExpandState: PropTypes.object,
  cancelLazyExpand: PropTypes.func,
  viewName: PropTypes.string,
  asOfDate: PropTypes.string
}
